import { Outlet, createFileRoute, useSearch } from '@tanstack/react-router'
import { useEffect } from 'react'
import { type Address } from 'viem'
import { useChainId, useConnect, useConnection } from 'wagmi'
import * as z from 'zod/mini'

import * as Auth from '#/lib/auth.js'
import { readOnly } from '#/lib/connectors/readOnly.js'
import { useMounted } from '#/lib/react.js'
import { useSyncTestnet } from '#/lib/testnet.js'
import { WithSidebar } from '#/routes/-layouts/WithSidebar.js'
import { WelcomePage } from '#/routes/welcome.js'

export const Route = createFileRoute('/_app')({
  component: AppLayout,
  validateSearch: z.looseObject({
    address: z.optional(z.string()),
    claim: z.optional(z.string()),
    redirect: z.optional(z.string()),
    testnet: z.optional(z.boolean()),
  }),
})

function AppLayout() {
  const mounted = useMounted()
  const me = Auth.me.useQuery()
  const { address } = useSearch({ from: '/_app' })
  const connection = useConnection()

  useSyncTestnet()

  if (!mounted) return null

  // Read-only mode: connect with the readOnly connector to view any address.
  if (address) return <ReadOnlyView address={address as Address} />

  if (connection.address)
    return (
      <WithSidebar>
        <Outlet />
      </WithSidebar>
    )

  if (me.isLoading) return null
  return <WelcomePage />
}

/** Auto-connects the readOnly connector and renders the dashboard. */
function ReadOnlyView(props: { address: Address }) {
  const chainId = useChainId()
  const { connectAsync } = useConnect()
  const connection = useConnection()
  const ready = connection.connector?.type === readOnly.type

  useEffect(() => {
    connectAsync({ chainId, connector: readOnly({ address: props.address }) })
  }, [props.address, chainId])

  if (!ready) return null
  return (
    <WithSidebar>
      <Outlet />
    </WithSidebar>
  )
}
